import { motion } from "framer-motion";

interface GanttRow {
  label: string;
  capa: string;
  start: number;
  duration: number;
  color: string;
}

const TOTAL_WEEKS = 16;

const rows: GanttRow[] = [
  { label: "T1 · Escucha y Arqueología de Marca", capa: "Capa 1", start: 1, duration: 2, color: "#534AB7" },
  { label: "T2 · Plataforma Estratégica y Posicionamiento", capa: "Capa 1", start: 3, duration: 2, color: "#185FA5" },
  { label: "T3 · Arquitectura y Relato de Origen", capa: "Capa 1", start: 5, duration: 2, color: "#1d4a8c" },
  { label: "T4 · Identidad Visual y Verbal", capa: "Capa 2", start: 7, duration: 4, color: "#0F6E56" },
  { label: "T5 · Sistema y Narrativa de Marca", capa: "Capa 2", start: 11, duration: 4, color: "#854F0B" },
  { label: "T6 · Activación: local, packaging y material de venta", capa: "Capa 2", start: 15, duration: 2, color: "#993C1D" },
];

const months = [
  { name: "Septiembre", span: 4 },
  { name: "Octubre", span: 4 },
  { name: "Noviembre", span: 4 },
  { name: "Diciembre", span: 4 },
];

const MammaCronograma = () => {
  return (
    <section
      id="cronograma"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, hsl(220 69% 2%), hsl(217 61% 10%))",
      }}
    >
      <div className="container px-4 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block"
            style={{ color: "#7ab7fd" }}
          >
            Cronograma
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Dos capas,
            <br />
            <span style={{ color: "#7ab7fd" }}>16 semanas de construcción.</span>
          </h2>
          <p className="italic text-base md:text-lg text-white/60 leading-relaxed max-w-2xl mx-auto">
            La Capa 1 se entrega en las primeras 6 semanas. La Capa 2 se activa al
            aprobarla, y continúa hasta cerrar el sistema completo.
          </p>
        </motion.div>

        {/* Gantt */}
        <div className="overflow-x-auto -mx-4 px-4 pb-4">
          <div
            className="min-w-[820px] rounded-2xl border border-white/10 p-5 md:p-8"
            style={{ background: "hsl(220 69% 4%)" }}
          >
            {/* Months */}
            <div
              className="grid mb-2"
              style={{ gridTemplateColumns: `280px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))` }}
            >
              <div />
              {months.map((m) => (
                <div
                  key={m.name}
                  className="text-xs md:text-sm font-semibold tracking-wider uppercase text-white/60 text-center border-b border-white/10 pb-2"
                  style={{ gridColumn: `span ${m.span} / span ${m.span}` }}
                >
                  {m.name}
                </div>
              ))}
            </div>

            {/* Weeks */}
            <div
              className="grid mb-5"
              style={{ gridTemplateColumns: `280px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))` }}
            >
              <div />
              {Array.from({ length: TOTAL_WEEKS }, (_, i) => (
                <div key={i} className="text-[10px] md:text-xs text-white/40 text-center">
                  S{i + 1}
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {rows.map((row, index) => (
                <div
                  key={row.label}
                  className="grid items-center"
                  style={{ gridTemplateColumns: `280px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))` }}
                >
                  <div className="pr-4">
                    <p className="text-sm md:text-[15px] text-white/85 font-medium leading-snug">
                      {row.label}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
                      {row.capa}
                    </p>
                  </div>

                  <div
                    className="relative h-10 rounded-md"
                    style={{
                      gridColumn: `span ${TOTAL_WEEKS} / span ${TOTAL_WEEKS}`,
                      background:
                        "repeating-linear-gradient(to right, rgba(255,255,255,0.03) 0 calc(100%/16), rgba(255,255,255,0.06) calc(100%/16) calc(100%/16 + 1px))",
                    }}
                  >
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute top-1 bottom-1 rounded-md flex items-center px-3"
                      style={{
                        left: `${((row.start - 1) / TOTAL_WEEKS) * 100}%`,
                        width: `${(row.duration / TOTAL_WEEKS) * 100}%`,
                        background: `linear-gradient(90deg, ${row.color}, ${row.color}cc)`,
                        transformOrigin: "left",
                        boxShadow: `0 6px 20px -8px ${row.color}`,
                      }}
                    >
                      <span className="text-[10px] md:text-xs font-semibold text-white/95 whitespace-nowrap">
                        {row.duration} sem
                      </span>
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Progress markers */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="max-w-3xl mx-auto mt-12"
        >
          <div className="flex items-center justify-between text-xs md:text-sm text-white/70 mb-3 font-medium">
            <span>Capa 1 · S1–S6</span>
            <span>Capa 2 · S7–S16</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div
              className="h-full w-full rounded-full"
              style={{ background: "linear-gradient(90deg, #4F46E5 0%, #7ab7fd 100%)" }}
            />
          </div>
          <p className="text-center text-xs text-white/50 mt-5">
            Inicio estimado: septiembre 2026 · Sistema completo: diciembre 2026
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MammaCronograma;
